import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { fieldService } from '@/services/fieldService';
import { wilayahService, KotaOption } from '@/services/wilayahService';
import { type FieldSummary } from '@/types/field';

// Helper to format Date to YYYY-MM-DD
const formatDateToISO = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Generate 14 days starting from today for day selector
const generateNext14Days = () => {
  const result = [];
  const days = ['MIN', 'SEN', 'SEL', 'RAB', 'KAM', 'JUM', 'SAB'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

  const today = new Date();
  for (let i = 0; i < 14; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    result.push({
      isoDate: formatDateToISO(d),
      dayName: days[d.getDay()],
      dateNum: String(d.getDate()).padStart(2, '0'),
      monthName: months[d.getMonth()],
      isToday: i === 0,
    });
  }
  return result;
};

// Location Dropdown Component hitting wilayahService.searchKota (min 3 chars)
const LocationSearchDropdown: React.FC<{
  value: string;
  onChange: (city: string) => void;
}> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const [options, setOptions] = useState<KotaOption[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (query.trim().length < 3) {
      setOptions([]);
      setLoading(false);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await wilayahService.searchKota(query);
        setOptions(res || []);
      } catch (err) {
        console.error('Failed to search kota:', err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (cityName: string) => {
    onChange(cityName);
    setQuery(cityName);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setQuery('');
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative min-w-[130px] sm:min-w-[150px]">
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="h-11 px-3.5 rounded-2xl border border-[#E0E6ED] bg-white flex items-center justify-between gap-2 cursor-pointer shadow-2xs hover:border-gray-300 transition-colors"
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <Icon icon="ph:map-pin-bold" className="w-4 h-4 text-gray-400 shrink-0" />
          <span className="text-xs font-semibold text-[#0E1726] truncate">
            {value || 'Lokasi'}
          </span>
        </div>
        <Icon
          icon="ph:caret-down-bold"
          className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-[#E0E6ED] rounded-2xl shadow-xl z-30 p-2 space-y-1 animate-in fade-in duration-150">
          <div className="relative">
            <input
              type="text"
              autoFocus
              placeholder="Ketik min. 3 huruf..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full h-8 pl-7 pr-7 rounded-xl bg-gray-50 border border-gray-200 text-xs text-[#0E1726] focus:outline-none focus:border-[#FC480D]"
            />
            <Icon icon="ph:magnifying-glass" className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-2.5" />
            {query && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
              >
                <Icon icon="ph:x-bold" className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="max-h-48 overflow-y-auto space-y-0.5 scrollbar-thin divide-y divide-gray-50">
            {value && (
              <button
                type="button"
                onClick={handleClear}
                className="w-full text-left px-2.5 py-1.5 text-xs text-rose-500 font-semibold hover:bg-rose-50 rounded-xl transition-colors"
              >
                Reset Lokasi
              </button>
            )}

            {loading ? (
              <div className="py-3 text-center text-xs text-gray-400 flex items-center justify-center gap-1.5">
                <Icon icon="ph:spinner-bold" className="w-3.5 h-3.5 animate-spin text-[#FC480D]" />
                Mencari kota...
              </div>
            ) : query.trim().length < 3 ? (
              <div className="py-2.5 px-2 text-[11px] text-gray-400 text-center">
                Ketik minimal 3 huruf kota
              </div>
            ) : options.length === 0 ? (
              <div className="py-2.5 px-2 text-xs text-gray-400 text-center">Kota tidak ditemukan</div>
            ) : (
              options.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => handleSelect(item.label)}
                  className="w-full text-left px-2.5 py-2 text-xs text-[#0E1726] hover:bg-orange-50 hover:text-[#FC480D] font-medium rounded-xl transition-colors flex items-center justify-between"
                >
                  <span className="truncate">{item.label}</span>
                  {value === item.label && (
                    <Icon icon="ph:check-bold" className="w-3.5 h-3.5 text-[#FC480D] shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Skeleton Card for Arena Loading
const ArenaCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-3xl p-4 sm:p-5 border border-[#E0E6ED] shadow-2xs space-y-4 animate-pulse">
    <div className="w-full h-44 rounded-2xl bg-gray-200" />
    <div className="space-y-2">
      <div className="h-5 bg-gray-200 rounded-md w-2/3" />
      <div className="h-4 bg-gray-200 rounded-md w-1/2" />
    </div>
    <div className="flex gap-2">
      <div className="h-6 w-20 bg-gray-200 rounded-full" />
      <div className="h-6 w-24 bg-gray-200 rounded-full" />
    </div>
    <div className="h-10 bg-gray-200 rounded-2xl w-full" />
  </div>
);

const ClientDiscoveryBookingPage: React.FC = () => {
  const navigate = useNavigate();

  const [daysList] = useState(generateNext14Days());
  const [selectedDate, setSelectedDate] = useState<string>(daysList[0].isoDate);
  const [selectedShift, setSelectedShift] = useState<'all' | 'pagi' | 'sore' | 'malam'>('sore');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(true);
  const [arenas, setArenas] = useState<FieldSummary[]>([]);

  // Mode Switcher Modal State
  const [isModeModalOpen, setIsModeModalOpen] = useState(false);
  const [isModeModalClosing, setIsModeModalClosing] = useState(false);

  const fetchArenas = useCallback(async () => {
    try {
      setLoading(true);
      setArenas([]);

      const data = await fieldService.fetchPage(1, {
        query: searchQuery || undefined,
        location: selectedLocation || undefined,
        date: selectedDate,
      });

      setArenas(data || []);
    } catch (err) {
      console.error('Failed to load arenas:', err);
      setArenas([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedLocation, selectedDate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchArenas();
    }, 250);
    return () => clearTimeout(timer);
  }, [fetchArenas]);

  const handleDateSelect = (isoDate: string) => {
    if (selectedDate === isoDate) return;
    setSelectedDate(isoDate);
  };

  const openModeModal = () => {
    setIsModeModalOpen(true);
    setIsModeModalClosing(false);
  };

  const closeModeModal = () => {
    if (isModeModalClosing) return;
    setIsModeModalClosing(true);
    setTimeout(() => {
      setIsModeModalOpen(false);
      setIsModeModalClosing(false);
    }, 280);
  };

  const handleSelectMode = (targetPath: string) => {
    closeModeModal();
    setTimeout(() => {
      navigate(targetPath);
    }, 200);
  };

  const handleBookArena = (slug: string) => {
    navigate(`/arena/${slug}`);
  };

  // Mock slot data per arena for slot pills preview
  const getTimeSlotsForShift = (shift: string) => {
    if (shift === 'pagi') return ['06.00', '07.00', '08.00', '16.00', '20.00'];
    if (shift === 'sore') return ['15.00', '16.00', '17.00', '19.00', '21.00'];
    return ['18.00', '19.00', '20.00', '21.00'];
  };

  // Filter & sort arenas
  const filteredArenas = arenas.filter((item) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchName = item.name?.toLowerCase().includes(q);
      const matchCity = item.maps?.city?.toLowerCase().includes(q);
      if (!matchName && !matchCity) return false;
    }
    if (selectedLocation) {
      const loc = selectedLocation.toLowerCase();
      const matchCity = item.maps?.city?.toLowerCase().includes(loc);
      const matchAddr = item.maps?.address?.toLowerCase().includes(loc);
      if (!matchCity && !matchAddr) return false;
    }
    return true;
  });

  return (
    <div className="bg-[#F8FAFC] min-h-screen text-[#0E1726] pb-24 md:pb-12">
      {/* ================= DESKTOP VIEW ================= */}
      <div className="hidden md:block max-w-6xl mx-auto px-6 pt-6 pb-8 space-y-6 animate-fade-in">
        {/* Header & Mode Switcher Bar */}
        <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-orange-50 border border-orange-100 flex items-center justify-center text-[#FC480D]">
              <Icon icon="ph:buildings-bold" className="w-6 h-6" />
            </div>
            <div>
              <p className="text-[11px] font-bold tracking-wider text-gray-400 uppercase">
                MODE PENCARIAN
              </p>
              <h1 className="text-xl font-extrabold text-[#0E1726]">
                Booking lapangan
              </h1>
            </div>
          </div>

          {/* Desktop Mode Navigation Pills */}
          <div className="flex items-center bg-gray-100/80 p-1.5 rounded-2xl border border-gray-200/60">
            <button
              type="button"
              onClick={() => navigate('/discovery/event')}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-600 hover:text-[#0E1726] hover:bg-white/60 transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Icon icon="ph:calendar-blank-bold" className="w-4 h-4" />
              <span>Cari event</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/discovery/community')}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-600 hover:text-[#0E1726] hover:bg-white/60 transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Icon icon="ph:users-three-bold" className="w-4 h-4" />
              <span>Cari komunitas</span>
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-xl text-xs font-bold bg-white text-[#FC480D] shadow-xs flex items-center gap-1.5"
            >
              <Icon icon="ph:buildings-bold" className="w-4 h-4" />
              <span>Booking lapangan</span>
            </button>
          </div>
        </div>

        {/* Desktop Filter Toolbar */}
        <div className="bg-white rounded-2xl border border-[#E0E6ED] p-3.5 shadow-2xs flex flex-wrap items-center justify-between gap-3">
          {/* Search Input & Location Dropdown */}
          <div className="flex items-center gap-2.5 flex-1 min-w-[320px] max-w-xl">
            <div className="relative flex-1">
              <Icon
                icon="ph:magnifying-glass-bold"
                className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
              />
              <input
                type="text"
                placeholder="Cari arena atau area..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-11 pl-9 pr-8 rounded-xl border border-[#E0E6ED] bg-gray-50/50 text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:bg-white shadow-2xs transition-colors"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-bold"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="w-48 shrink-0">
              <LocationSearchDropdown
                value={selectedLocation}
                onChange={setSelectedLocation}
              />
            </div>
          </div>

          {/* Shift Time Filter Pills */}
          <div className="flex items-center gap-2">
            {[
              { id: 'pagi', label: 'Pagi', icon: 'ph:sun-horizon-bold' },
              { id: 'sore', label: 'Sore', icon: 'ph:sun-bold' },
              { id: 'malam', label: 'Malam', icon: 'ph:moon-bold' },
            ].map((shift) => {
              const isSelected = selectedShift === shift.id;
              return (
                <button
                  key={shift.id}
                  type="button"
                  onClick={() => setSelectedShift(shift.id as any)}
                  className={`h-11 px-4 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all duration-200 cursor-pointer shadow-2xs border ${
                    isSelected
                      ? 'bg-orange-50/80 border-[#FC480D] text-[#FC480D]'
                      : 'bg-white border-[#E0E6ED] text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon icon={shift.icon} className={`w-4 h-4 ${isSelected ? 'text-[#FC480D]' : 'text-gray-400'}`} />
                  <span>{shift.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Day Selector Strip */}
        <div className="bg-white rounded-2xl border border-[#E0E6ED] p-3 shadow-2xs">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {daysList.map((item) => {
              const isSelected = selectedDate === item.isoDate;
              return (
                <button
                  key={item.isoDate}
                  type="button"
                  onClick={() => handleDateSelect(item.isoDate)}
                  className={`flex flex-col items-center justify-center flex-1 min-w-[70px] h-16 rounded-xl shrink-0 transition-all duration-200 cursor-pointer border ${
                    isSelected
                      ? 'bg-[#FC480D] text-white border-[#FC480D] shadow-sm font-bold scale-[1.02]'
                      : 'bg-gray-50/60 text-[#0E1726] border-gray-100 hover:bg-gray-100/80'
                  }`}
                >
                  <span className={`text-[10px] font-bold tracking-wider ${isSelected ? 'text-white/90' : 'text-gray-400'}`}>
                    {item.dayName}
                  </span>
                  <span className="text-base font-extrabold leading-tight mt-0.5">
                    {item.dateNum}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Section Header */}
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-2.5">
            <h2 className="text-base font-bold text-[#0E1726]">
              Arena & Lapangan Tersedia
            </h2>
            {!loading && (
              <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-orange-50 text-[#FC480D] border border-orange-100">
                {filteredArenas.length} arena
              </span>
            )}
          </div>
        </div>

        {/* Desktop Arenas Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <ArenaCardSkeleton />
            <ArenaCardSkeleton />
            <ArenaCardSkeleton />
            <ArenaCardSkeleton />
          </div>
        ) : filteredArenas.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 border border-[#E0E6ED] text-center space-y-2 shadow-2xs">
            <Icon icon="ph:buildings-bold" className="w-12 h-12 text-gray-300 mx-auto" />
            <h3 className="text-sm font-bold text-[#0E1726]">Arena Tidak Ditemukan</h3>
            <p className="text-xs text-gray-400 max-w-xs mx-auto">
              Tidak ada arena atau lapangan yang sesuai dengan tanggal dan lokasi yang Anda pilih.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 animate-in fade-in duration-300">
            {filteredArenas.map((arena, idx) => {
              const mainImage = arena.images?.images?.[0] || '';
              const formattedPrice = arena.min_price
                ? `Rp. ${Math.round(arena.min_price / 1000)}rb`
                : 'Rp. 150rb';

              const cityLocation = arena.maps?.kecamatan
                ? `${arena.maps.kecamatan} · ${arena.maps.city || 'Kab. Sleman'}`
                : arena.maps?.city || 'Yogyakarta';

              const slots = getTimeSlotsForShift(selectedShift);
              const activeDateObj = daysList.find((d) => d.isoDate === selectedDate);
              const dateDisplay = activeDateObj ? `${activeDateObj.dateNum} ${activeDateObj.monthName}` : 'Hari ini';

              return (
                <div
                  key={arena.slug || idx}
                  className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs hover:shadow-md hover:border-orange-200 hover:-translate-y-0.5 transition-all space-y-4 flex flex-col justify-between"
                >
                  <div className="space-y-3.5">
                    {/* Hero Banner Image */}
                    <div
                      onClick={() => handleBookArena(arena.slug)}
                      className="relative w-full h-48 rounded-2xl overflow-hidden bg-gray-100 group cursor-pointer"
                    >
                      {mainImage ? (
                        <img
                          src={mainImage}
                          alt={arena.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#FC480D]/20 to-gray-200" />
                      )}
                    </div>

                    {/* Arena Title & Location */}
                    <div className="space-y-1">
                      <h3
                        onClick={() => handleBookArena(arena.slug)}
                        className="text-lg font-extrabold text-[#0E1726] hover:text-[#FC480D] cursor-pointer transition-colors truncate"
                      >
                        {arena.name}
                      </h3>
                      <div className="flex items-center gap-1.5 text-xs text-gray-400 font-semibold truncate">
                        <Icon icon="ph:map-pin-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span>{cityLocation}</span>
                      </div>
                    </div>

                    {/* Available Time Slots Section */}
                    <div className="space-y-2 pt-1">
                      <div className="text-xs font-bold text-[#0E1726]">
                        Jam tersedia · {dateDisplay}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {slots.map((slot, sIdx) => {
                          const isBooked = sIdx === 1 || sIdx === 2 || sIdx === 3;
                          return (
                            <div
                              key={slot}
                              className={`px-3.5 py-1.5 rounded-full text-xs transition-all ${
                                isBooked
                                  ? 'bg-gray-100/70 border border-gray-100 text-gray-300 font-bold line-through'
                                  : 'bg-white border border-gray-300 text-[#0E1726] font-extrabold shadow-2xs'
                              }`}
                            >
                              {slot}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3.5 pt-2">
                    <hr className="border-gray-100" />

                    {/* Bottom Row: Price & Booking Button */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="space-y-0.5">
                        <span className="text-xs font-semibold text-gray-400">Mulai dari </span>
                        <span className="text-[#FC480D] font-extrabold text-base">{formattedPrice}</span>
                        <span className="text-xs font-semibold text-gray-400"> /jam</span>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleBookArena(arena.slug)}
                        className="bg-[#FC480D] hover:bg-[#D83B08] text-white text-xs font-extrabold px-5 py-2.5 rounded-full flex items-center gap-1.5 shadow-md hover:shadow-lg transition-all active:scale-98 cursor-pointer"
                      >
                        <Icon icon="ph:calendar-check-bold" className="w-4 h-4" />
                        <span>Booking</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ================= MOBILE VIEW (100% UNTOUCHED) ================= */}
      <div className="md:hidden">
        <div className="max-w-2xl mx-auto px-4 pt-4 md:pt-6 space-y-4 animate-fade-in">
        {/* Top Card: MODE PENCARIAN */}
        <div className="bg-white rounded-3xl p-4 sm:p-5 border border-[#E0E6ED] shadow-2xs flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-orange-50 border border-orange-100 flex items-center justify-center text-[#FC480D]">
              <Icon icon="ph:buildings-bold" className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] font-bold tracking-wider text-gray-400 uppercase">
                MODE PENCARIAN
              </p>
              <h2 className="text-sm sm:text-base font-extrabold text-[#0E1726]">
                Booking lapangan
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={openModeModal}
            className="h-9 px-3.5 rounded-full bg-orange-50 hover:bg-orange-100 border border-orange-100 text-[#FC480D] text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer active:scale-95"
          >
            <span>Ganti</span>
            <Icon icon="ph:arrows-clockwise-bold" className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Search & Location Bar */}
        <div className="flex items-center gap-2.5">
          <div className="relative flex-1">
            <Icon
              icon="ph:magnifying-glass"
              className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5"
            />
            <input
              type="text"
              placeholder="Cari arena atau area"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-11 pl-9 pr-3 rounded-2xl border border-[#E0E6ED] bg-white text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] shadow-2xs transition-colors"
            />
          </div>
          <LocationSearchDropdown
            value={selectedLocation}
            onChange={setSelectedLocation}
          />
        </div>

        {/* Day Pills Row */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {daysList.map((item) => {
            const isSelected = selectedDate === item.isoDate;
            return (
              <button
                key={item.isoDate}
                type="button"
                onClick={() => handleDateSelect(item.isoDate)}
                className={`flex flex-col items-center justify-center w-14 h-16 rounded-2xl shrink-0 transition-all duration-200 cursor-pointer shadow-2xs active:scale-95 border ${
                  isSelected
                    ? 'bg-[#FC480D] text-white border-[#FC480D] shadow-orange-200/60 scale-102 font-bold'
                    : 'bg-white text-[#0E1726] border-[#E0E6ED] hover:bg-gray-50'
                }`}
              >
                <span className={`text-[10px] font-bold tracking-wider ${isSelected ? 'text-white/90' : 'text-gray-400'}`}>
                  {item.dayName}
                </span>
                <span className="text-base font-extrabold leading-tight mt-0.5">
                  {item.dateNum}
                </span>
              </button>
            );
          })}
        </div>

        {/* Shift Time Filter Pills */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {[
            { id: 'pagi', label: 'Pagi', icon: 'ph:sun-horizon-bold' },
            { id: 'sore', label: 'Sore', icon: 'ph:sun-bold' },
            { id: 'malam', label: 'Malam', icon: 'ph:moon-bold' },
          ].map((shift) => {
            const isSelected = selectedShift === shift.id;
            return (
              <button
                key={shift.id}
                type="button"
                onClick={() => setSelectedShift(shift.id as any)}
                className={`h-10 px-3 rounded-2xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all duration-200 cursor-pointer shadow-2xs border ${
                  isSelected
                    ? 'bg-orange-50/80 border-[#FC480D] text-[#FC480D]'
                    : 'bg-white border-[#E0E6ED] text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon icon={shift.icon} className={`w-4 h-4 ${isSelected ? 'text-[#FC480D]' : 'text-gray-400'}`} />
                <span>{shift.label}</span>
              </button>
            );
          })}
        </div>

        {/* Count Header */}
        <div className="pt-1">
          <span className="text-xs font-bold text-gray-500">
            {loading ? 'Memuat arena...' : `${filteredArenas.length} arena tersedia`}
          </span>
        </div>

        {/* Arenas List */}
        {loading ? (
          <div className="space-y-4">
            <ArenaCardSkeleton />
            <ArenaCardSkeleton />
          </div>
        ) : filteredArenas.length === 0 ? (
          <div className="bg-white rounded-3xl p-8 border border-[#E0E6ED] text-center space-y-2 shadow-2xs">
            <Icon icon="ph:buildings-bold" className="w-10 h-10 text-gray-300 mx-auto" />
            <h3 className="text-sm font-bold text-[#0E1726]">Arena Tidak Ditemukan</h3>
            <p className="text-xs text-gray-400 max-w-xs mx-auto">
              Tidak ada arena atau lapangan yang tersedia sesuai dengan tanggal dan lokasi yang Anda pilih.
            </p>
          </div>
        ) : (
          <div className="space-y-4 animate-in fade-in duration-300">
            {filteredArenas.map((arena, idx) => {
              const mainImage = arena.images?.images?.[0] || '';
              const formattedPrice = arena.min_price
                ? `Rp. ${Math.round(arena.min_price / 1000)}rb`
                : 'Rp. 150rb';

              const cityLocation = arena.maps?.kecamatan
                ? `${arena.maps.kecamatan} · ${arena.maps.city || 'Kab. Sleman'}`
                : arena.maps?.city || 'Yogyakarta';

              const slots = getTimeSlotsForShift(selectedShift);
              const activeDateObj = daysList.find((d) => d.isoDate === selectedDate);
              const dateDisplay = activeDateObj ? `${activeDateObj.dateNum} ${activeDateObj.monthName}` : 'Hari ini';

              return (
                <div
                  key={arena.slug || idx}
                  className="bg-white rounded-3xl p-4 sm:p-5 border border-[#E0E6ED] shadow-2xs hover:shadow-xs transition-all duration-200 space-y-3.5"
                >
                  {/* Hero Banner Image */}
                  <div className="relative w-full h-44 sm:h-48 rounded-2xl overflow-hidden bg-gray-200 group">
                    {mainImage ? (
                      <img
                        src={mainImage}
                        alt={arena.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#FC480D]/20 to-gray-200" />
                    )}
                  </div>

                  {/* Arena Title & Location */}
                  <div className="space-y-1">
                    <h3
                      onClick={() => handleBookArena(arena.slug)}
                      className="text-lg font-extrabold text-[#0E1726] hover:text-[#FC480D] cursor-pointer transition-colors truncate"
                    >
                      {arena.name}
                    </h3>
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 font-semibold truncate">
                      <Icon icon="ph:map-pin-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span>{cityLocation}</span>
                    </div>
                  </div>

                  {/* Available Time Slots Section */}
                  <div className="space-y-2 pt-1">
                    <div className="text-xs font-bold text-[#0E1726]">
                      Jam tersedia · {dateDisplay}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {slots.map((slot, sIdx) => {
                        const isBooked = sIdx === 1 || sIdx === 2 || sIdx === 3;
                        return (
                          <div
                            key={slot}
                            className={`px-3.5 py-1.5 rounded-full text-xs transition-all ${
                              isBooked
                                ? 'bg-gray-100/70 border border-gray-100 text-gray-300 font-bold line-through'
                                : 'bg-white border border-gray-300 text-[#0E1726] font-extrabold shadow-2xs'
                            }`}
                          >
                            {slot}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <hr className="border-gray-100" />

                  {/* Bottom Row: Price & Booking Button */}
                  <div className="flex items-center justify-between gap-3 pt-1">
                    <div className="space-y-0.5">
                      <span className="text-xs font-semibold text-gray-400">Mulai dari </span>
                      <span className="text-[#FC480D] font-extrabold text-base">{formattedPrice}</span>
                      <span className="text-xs font-semibold text-gray-400"> /jam</span>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleBookArena(arena.slug)}
                      className="bg-[#FC480D] hover:bg-[#D83B08] text-white text-xs font-extrabold px-4 py-2.5 rounded-full flex items-center gap-1.5 shadow-md hover:shadow-lg transition-all active:scale-98 cursor-pointer"
                    >
                      <Icon icon="ph:calendar-check-bold" className="w-4 h-4" />
                      <span>Booking</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>

      {/* Change Mode Bottom Sheet Popup Modal */}
      {isModeModalOpen && (
        <div
          className={`fixed inset-0 z-50 bg-[#0E1726]/60 backdrop-blur-xs flex items-end justify-center transition-opacity duration-300 ease-in-out ${
            isModeModalClosing ? 'opacity-0 pointer-events-none' : 'opacity-100 animate-in fade-in duration-200'
          }`}
          onClick={closeModeModal}
        >
          <div
            className={`w-full max-w-md bg-white rounded-t-3xl p-5 sm:p-6 flex flex-col gap-4 shadow-2xl transition-transform duration-300 ease-in-out ${
              isModeModalClosing
                ? 'translate-y-full'
                : 'translate-y-0 animate-in slide-in-from-bottom duration-300'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle bar */}
            <div className="w-12 h-1 rounded-full bg-gray-300 self-center -mt-1 mb-1" />

            <div className="space-y-1">
              <h2 className="text-lg sm:text-xl font-bold text-[#0E1726]">
                Pilih Mode Pencarian
              </h2>
              <p className="text-xs text-gray-400 font-medium">
                Pilih satu untuk melihat jenis penjelajahan lain
              </p>
            </div>

            <div className="space-y-3 pt-1">
              {[
                {
                  id: 'event',
                  title: 'Cari event',
                  subtitle: 'Mabar, sparing & turnamen di sekitarmu',
                  icon: 'ph:calendar-blank-bold',
                  bgColor: 'bg-orange-50 border-orange-100',
                  iconColor: 'text-[#FC480D]',
                  path: '/discovery/event',
                },
                {
                  id: 'community',
                  title: 'Cari komunitas',
                  subtitle: 'Gabung grup yang rutin main tiap pekan',
                  icon: 'ph:users-three-bold',
                  bgColor: 'bg-blue-50 border-blue-100',
                  iconColor: 'text-blue-600',
                  path: '/discovery/community',
                },
                {
                  id: 'booking',
                  title: 'Booking lapangan',
                  subtitle: 'Sewa lapangan sesuai jam & lokasi',
                  icon: 'ph:buildings-bold',
                  bgColor: 'bg-emerald-50 border-emerald-100',
                  iconColor: 'text-emerald-600',
                  path: '/discovery/arena',
                },
              ].map((opt) => (
                <div
                  key={opt.id}
                  onClick={() => handleSelectMode(opt.path)}
                  className="w-full p-4 rounded-2xl bg-white border border-[#E0E6ED] shadow-2xs flex items-center justify-between transition-all cursor-pointer group active:scale-[0.99]"
                >
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border ${opt.bgColor} ${opt.iconColor}`}
                    >
                      <Icon icon={opt.icon} className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-[#0E1726] group-hover:text-[#FC480D] transition-colors truncate">
                        {opt.title}
                      </h3>
                      <p className="text-xs text-gray-400 font-medium truncate mt-0.5">
                        {opt.subtitle}
                      </p>
                    </div>
                  </div>
                  <Icon
                    icon="ph:caret-right-bold"
                    className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors shrink-0"
                  />
                </div>
              ))}
            </div>

            <div className="pt-2 text-center">
              <button
                type="button"
                onClick={closeModeModal}
                className="text-xs font-semibold text-gray-500 hover:text-[#0E1726] transition-colors py-2 px-4 rounded-full cursor-pointer"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDiscoveryBookingPage;
